import * as _ from 'lodash'
import * as React from 'react'

export interface ReactError extends Error {
  columnNumber: number,
  fileName: string,
  lineNumber: number,
  message: string,
  children: React.ReactNode
}

export interface ReactErrorInfo {
  componentStack: string
}

export interface ErrorBoundaryProps {
  devLayoutFn?: (input: React.ReactNode) => React.ReactNode
  prodLayoutFn?: (input: React.ReactNode) => React.ReactNode
  children: React.ReactNode
}

export interface ErrorBoundaryState {
  error: ReactError | null,
  info: ReactErrorInfo | null,
  showError: boolean,
}

export class ErrorBoundary extends React.PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  public static defaultProps = {
    devLayoutFn: (input: React.ReactNode) => (
      <div className='full-center-wrapper flex justify-center'>
        <div className='full-center flex-auto'>
          {input}
        </div>
      </div>
    ),
    prodLayoutFn: (input: React.ReactNode) => (
      <div className='full-center-wrapper flex justify-center'>
        <div className='full-center flex-auto'>
          <div className='text-3xl font-bold text-center'>Something Went Wrong</div>

          <p className='text-center'>Let us know how it happened, and we will fix it.</p>
        </div>
      </div>
    ),
  }

  constructor (props: ErrorBoundaryProps) {
    super(props)

    this.state = {
      error: null,
      info: null,
      showError: false,
    }

    _.bindAll(this, [
      'renderError',
      'renderDevError',
    ])
  }

  public componentDidCatch (error: ReactError, info: ReactErrorInfo) {
    this.setState({
      error,
      info,
      showError: true,
    })

    // console.error(error)
    // console.info('error-info', info.componentStack)
  }

  public render (): React.ReactNode {
    if (this.state.showError) {
      return this.renderError()
    }

    return this.props.children
  }

  private renderError (): React.ReactNode {
    return (window._env_?.NODE_ENV !== 'production')
      ? this.renderDevError()
      : this.renderProdError()
  }

  private renderDevError (): React.ReactNode {
    if (this.state.error === null || this.state.info === null) {
      return "So, there's an error...but we don't know what it is..."
    }

    const message = 'Something went wrong.'

    const errorInfo = this.state.info.componentStack
    const displayError = {
      column: this.state.error.columnNumber,
      file: this.state.error.fileName,
      line: this.state.error.lineNumber,
    }

    return this.props.devLayoutFn!(
      (<React.Fragment>
        <div className='text-3xl font-bold text-center'>{message}</div>

        <h3 className='text-center'>{this.state.error.message}</h3>

        <dl>
          <dt>Location</dt>
          <dd>
            <pre>{JSON.stringify(displayError, null, 2)}</pre>
          </dd>

          <dt>Stack Trace</dt>
          <dd>
            <pre>{errorInfo}</pre>
          </dd>
        </dl>
      </React.Fragment>))
  }

  private renderProdError (): React.ReactNode {
    return this.props.prodLayoutFn!(<React.Fragment />)
  }
}

export default ErrorBoundary